/**
 * Mobile push-enable prompt: a one-time bottom sheet shown after the app opens, asking the
 * user to turn on notifications. Only appears when push is supported, on a mobile/standalone
 * viewport, permission is not already denied, there is no existing subscription, and the user
 * hasn't dismissed it before. "Enable" runs the shared subscribe flow; "Later" suppresses it.
 */
import { useEffect, useState } from 'react'
import { pushSupported, isPushSubscribed, enablePush } from '../lib/push-client'
import { useT } from '../i18n/language-provider'
import { Button } from './ui/button'

const DISMISS_KEY = 'push-prompt-dismissed-v1'

function isMobileOrStandalone(): boolean {
  return (
    window.matchMedia('(max-width: 768px)').matches ||
    window.matchMedia('(display-mode: standalone)').matches ||
    // iOS standalone flag
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  )
}

export function PushEnablePrompt() {
  const t = useT()
  const [visible, setVisible] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (!pushSupported) return
    if (!isMobileOrStandalone()) return
    if (Notification.permission === 'denied') return
    // localStorage can throw (private mode / quota) and there is no root ErrorBoundary to catch it;
    // fall back to "not dismissed" so the prompt logic still runs.
    let dismissed = false
    try { dismissed = !!localStorage.getItem(DISMISS_KEY) } catch { dismissed = false }
    if (dismissed) return
    let alive = true
    void isPushSubscribed().then((sub) => {
      if (alive && !sub) setVisible(true)
    })
    return () => { alive = false }
  }, [])

  function dismiss() {
    // Swallow: a blocked write (private mode / quota) must not break dismissing the prompt.
    try { localStorage.setItem(DISMISS_KEY, '1') } catch { /* ignore */ }
    setVisible(false)
  }

  async function onEnable() {
    setBusy(true)
    await enablePush()
    setBusy(false)
    // Enabled or permission denied → stop prompting either way.
    dismiss()
  }

  if (!visible) return null

  return (
    // z-30: above the page, below a popup (Modal is z-40, its confirmations z-50) and the mobile menu. At z-50 it sat
    // over the bottom of every popup, a bottom sheet on a phone, and took the clicks meant for its buttons.
    <div id="push-enable-prompt" className="fixed inset-x-0 bottom-0 z-30 p-3 sm:mx-auto sm:max-w-md">
      <div className="rounded-xl border border-border bg-surface p-4 shadow-xl">
        <p className="mb-1 text-sm font-semibold text-fg">{t('push.promptTitle')}</p>
        <p className="mb-3 text-xs text-muted">{t('push.promptBody')}</p>
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="md" type="button"
            onClick={dismiss}
            disabled={busy}>
            {t('push.later')}
          </Button>
          <button
            type="button"
            onClick={() => void onEnable()}
            disabled={busy}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-white hover:opacity-90 disabled:opacity-50"
          >
            {t('push.enable')}
          </button>
        </div>
      </div>
    </div>
  )
}
